import { useCallback, useEffect, useMemo } from "react";

import { useActiveDataset } from "../data-hub-library/hooks/useActiveDataset";
import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  computeDataHubRoScores,
  fetchDataHubRoConfig,
  resetDataHubRoConfig,
  saveDataHubRoConfig,
} from "../Store/Reducers/DataHubSlice";
import type {
  RoCompositeScore,
  RoComputeDimension,
  RoConfig,
  RoConfigPayload,
  RoDimensionScore,
} from "../Types/RoConfig";

/** ApiRequest throws `Error(JSON.stringify({ message, status, ... }))` on HTTP errors. */
export function roConfigErrorMessage(error: unknown, fallback: string): string {
  const e = error as any;
  const fromAxios = e?.response?.data?.response?.message;
  if (typeof fromAxios === "string" && fromAxios.trim()) return fromAxios;
  const raw = e?.message;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.message === "string" && parsed.message.trim()) {
        return parsed.message;
      }
    } catch {
      return raw;
    }
  }
  return fallback;
}

/** Local R&O config — GET /metrics/ro/config (tenant DB, not XFactor). */
export const useRoConfig = () => {
  const dispatch = useAppDispatch();
  const { data, loading, error, success } = useAppSelector(
    state => state.dataHub.roConfig
  );

  const reload = useCallback(() => {
    void dispatch(fetchDataHubRoConfig());
  }, [dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Panels read `e.message` from a failed save/reset.
  const saveConfig = useCallback(
    async (payload: RoConfigPayload): Promise<RoConfig> => {
      const result = await dispatch(saveDataHubRoConfig(payload));
      if (saveDataHubRoConfig.rejected.match(result)) {
        throw new Error(result.payload || "Failed to save R&O config.");
      }
      return result.payload;
    },
    [dispatch]
  );

  const resetConfig = useCallback(async (): Promise<RoConfig> => {
    const result = await dispatch(resetDataHubRoConfig());
    if (resetDataHubRoConfig.rejected.match(result)) {
      throw new Error(result.payload || "Failed to reset R&O config.");
    }
    return result.payload;
  }, [dispatch]);

  return {
    riskDimensions: data.riskDimensions,
    opportunityDimensions: data.opportunityDimensions,
    metrics: data.metrics,
    dimensionWeights: data.dimensionWeights,
    loading: loading || (!success && !error),
    error,
    reload,
    saveConfig,
    resetConfig,
  };
};

/** Thin wrappers so existing panels can keep their hook names during migration. */
export const useRoDimensions = () => {
  const { riskDimensions, opportunityDimensions, loading, error, reload } =
    useRoConfig();
  return { riskDimensions, opportunityDimensions, loading, error, reload };
};

export const useRoMetrics = () => {
  const { metrics, loading, error, reload } = useRoConfig();
  return { metrics, loading, error, reload };
};

export const useRoWeights = () => {
  const {
    dimensionWeights,
    metrics,
    loading,
    error,
    reload,
    saveConfig,
    resetConfig,
  } = useRoConfig();
  return {
    dimensionWeights,
    metrics,
    loading,
    error,
    reload,
    saveConfig,
    resetConfig,
  };
};

const RCS_BANDS: { code: string; min: number; max: number; label: string }[] = [
  { code: "R5", min: 0, max: 19, label: "Critical" },
  { code: "R4", min: 20, max: 39, label: "High" },
  { code: "R3", min: 40, max: 59, label: "Moderate" },
  { code: "R2", min: 60, max: 79, label: "Low" },
  { code: "R1", min: 80, max: 100, label: "Minimal" },
];

const OCS_BANDS: { code: string; min: number; max: number; label: string }[] = [
  { code: "O1", min: 0, max: 19, label: "Minimal" },
  { code: "O2", min: 20, max: 39, label: "Low" },
  { code: "O3", min: 40, max: 59, label: "Moderate" },
  { code: "O4", min: 60, max: 79, label: "High" },
  { code: "O5", min: 80, max: 100, label: "Very High" },
];

function bandColor(score: number): "green" | "amber" | "red" {
  if (score >= 66) return "green";
  if (score >= 33) return "amber";
  return "red";
}

function bandMeta(
  score: number | null | undefined,
  bands: typeof RCS_BANDS,
  codeHint?: string | null
): RoCompositeScore {
  const s = score ?? 0;
  const fromCode = codeHint ? bands.find(b => b.code === codeHint) : undefined;
  const fromScore =
    fromCode ||
    bands.find(b => s >= b.min && s <= (b.max >= 100 ? 100 : b.max));
  const code = fromScore?.code ?? codeHint ?? "—";
  const label = fromScore?.label ?? "";
  return {
    score: s,
    bandLabel: label ? `${code} · ${label}` : String(code),
    band: bandColor(s),
  };
}

const toDimensionScore = (d: RoComputeDimension): RoDimensionScore => ({
  key: d.key,
  label: d.label ?? d.key,
  sharePct: Number(((d.effective_share ?? 0) * 100).toFixed(1)),
  n: d.n ?? 0,
  score: d.score ?? 0,
  band: bandColor(d.score ?? 0),
  included: d.score != null,
});

/** Live local R&O scores via POST /metrics/ro/compute?persist=false */
export const useRoScores = () => {
  const dispatch = useAppDispatch();
  const [dataset] = useActiveDataset();
  const { data, loading, error, success } = useAppSelector(
    state => state.dataHub.roScores
  );

  useEffect(() => {
    void dispatch(computeDataHubRoScores());
  }, [dispatch, dataset]);

  const scores = useMemo(
    () => ({
      riskComposite: data ? bandMeta(data.rcs, RCS_BANDS, data.rcs_band) : null,
      opportunityComposite: data
        ? bandMeta(data.ocs, OCS_BANDS, data.ocs_band)
        : null,
      riskDimensionScores: (data?.risk_dimensions ?? []).map(toDimensionScore),
      opportunityDimensionScores: (data?.opportunity_dimensions ?? []).map(
        toDimensionScore
      ),
    }),
    [data]
  );

  return {
    ...scores,
    loading: loading || (!success && !error),
    error,
  };
};

// Back-compat aliases for panels that still import the old XFactor names.
export const useXFactorRoDimensions = useRoDimensions;
export const useXFactorRoMetrics = useRoMetrics;
export const useXFactorRoWeights = useRoWeights;
export const useXFactorRoScores = useRoScores;
